import { BufferedEventBus } from '../utils/BufferedEventBus';
import { Logger } from '../support/Logger';
import { IMediaTokenSession, IMediaTokenSessions, MediaTokenSessions } from './MediaTokenSessions';

// Refreshed this long before it actually expires, so a token is never handed to an <img> moments
// before it stops working — the image would simply fail to load with nothing to retry it.
const REFRESH_MARGIN_MS = 60 * 1000;

interface ISessionTokenState {
    token?: string;
    expiresAtMs: number;
    inFlight?: Promise<string | undefined>;
}

// Holds the short-lived credential the media routes accept, and trades the session for a new one
// when it is close to expiring.
//
// The point of the trade is that this token reaches only the media routes. It travels in a url,
// because <img> and CSS background-image cannot set headers, and urls end up in browser history,
// referrer headers, access logs, and Mixpanel session replay — replay masks text only, so it
// records an image src and an inline background-image url in full. Note the scope is the routes, not the one image it was minted
// for: it reaches whatever media this principal could already read, attachments included. What it
// cannot do is reach anything else, which is why it is traded for rather than reusing the account's
// access token, and why it lasts minutes.
//
// Held per session, the one serving the media's href: an anonymous viewer has a session per care map
// they were sent, and each care map's media accepts only its own session's token. Within a session
// one in-flight request is shared: a care map paints many faces at once, and each would otherwise
// mint its own token.
export class MediaTokenProvider {

    static inject = () => [MediaTokenSessions, BufferedEventBus, Logger];
    constructor(
        private mediaTokenSessions: IMediaTokenSessions,
        private events: BufferedEventBus,
        private log: Logger
    ) {}

    // Signing out reloads the page, which would discard this anyway — but a cached credential for
    // the previous principal must not depend on that for its removal, and the reload is a detail
    // of how logout happens rather than a guarantee.
    init = () => {
        this.events.on('AuthService.signing-out', () => {
            this.clear();
        });
    };

    private sessionStates = new Map<string, ISessionTokenState>();
    private generation = 0;

    // The token for the session serving `href`.
    //
    // Undefined when a token cannot be obtained, which callers treat as a FAILURE rather than as
    // permission to read the object another way: buildAuthorizedMediaUrl throws when a download
    // link was offered and no token arrived. It used to fall back to the stored bucket url, which
    // turned every failed mint into a silent unaudited read; the degradation now is a missing image
    // and a Sentry error, which is the honest outcome. Throws when no session serves the href.
    get = async (href: string): Promise<string | undefined> => {
        const session = this.mediaTokenSessions.getSessionServing(href);

        let state = this.sessionStates.get(session.key);
        if (!state) {
            state = { expiresAtMs: 0 };
            this.sessionStates.set(session.key, state);
        }

        if (state.token && Date.now() < state.expiresAtMs - REFRESH_MARGIN_MS) {
            return state.token;
        }

        if (!state.inFlight) {
            const sessionState = state;
            sessionState.inFlight = this.fetch({ apiClient: session.apiClient, state: sessionState }).finally(() => {
                sessionState.inFlight = undefined;
            });
        }
        return await state.inFlight;
    };

    // Dropped on sign-out so a later principal cannot reuse the previous one's credentials. The
    // generation bump is what makes that hold when a mint is already in flight: without it the
    // request settles after this ran and returns the signed-out principal's token, which is exactly
    // the outcome the removal is not supposed to depend on a reload for.
    //
    // In-flight mints are dropped with the tokens. The generation bump stops them being used, but a
    // caller arriving before one settles would otherwise join it and receive the undefined it now
    // returns, a refused read for whoever comes next. The principal that replaces the one signing out
    // is usually an anonymous viewer, so dropping rather than lingering is what stops a token being
    // reused across identities.
    clear = () => {
        this.generation++;
        this.sessionStates = new Map();
    };

    private fetch = async (params: { apiClient: IMediaTokenSession['apiClient']; state: ISessionTokenState }): Promise<string | undefined> => {
        const { apiClient, state } = params;
        const generation = this.generation;
        try {
            const entry = await apiClient.entry();
            if (!entry.links?.mediaToken) {
                // An API older than the media routes. Nothing to trade for.
                return undefined;
            }

            const { token, expiresAt } = await apiClient.post(entry.links.mediaToken, {});

            if (generation !== this.generation) {
                // Signed out while this was in flight. The token belongs to a principal who is
                // gone, so it is neither cached nor returned.
                return undefined;
            }

            state.token = token;
            state.expiresAtMs = new Date(expiresAt).valueOf();
            return token;
        } catch (e) {
            this.log.error(e as Error, { context: 'MediaTokenProvider could not mint a media token' });
            return undefined;
        }
    };
}
